/// <amd-module name="Feed/Legacy/DataServices/FollowsDataService" />
import { Services, VssService, IVssService } from "VSS/Platform/Context";

import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

import { Package } from "Feed/Common/Contracts/Feed";
import { FeedServiceInstanceId } from "Feed/Common/Constants/Constants";

import { INotificationRestClient } from "Notifications/Common/RestClient/Notification";

import {
    SubscriptionQuery,
    SubscriptionQueryCondition,
    SubscriptionQueryFlags,
    NotificationSubscription,
    ArtifactFilter
} from "Notifications/Common/Contracts/Notification";
import { IVssPageService, IPageData } from "VSS/Platform/Page";

export interface IFeedFollowsDataService extends IVssService {
    getFollowState(packageSummary: Package, feed: Feed): Promise<NotificationSubscription>;
    follow(packageSummary: Package, feed: Feed): Promise<NotificationSubscription>;
    unfollow(subscriptionId: string): Promise<void>;
}

class FollowsDataService extends VssService {
    private static readonly ArtifactType: string = "Package";
    private static readonly FollowsType = "Artifact";

    public getFollowState(packageSummary: Package, feed: Feed): Promise<NotificationSubscription> {
        if (!packageSummary || !feed) {
            return Promise.reject();
        }

        const artifactId: string = this.getArtifactId(feed, packageSummary);
        const subQuery: SubscriptionQuery | null = this.getSubscriptionQuery(
            FollowsDataService.ArtifactType,
            artifactId
        );

        return this.NotificationRestClient.querySubscriptions(subQuery).then(
            (subscriptions: NotificationSubscription[]) => {
                if (subscriptions != null && subscriptions.length > 0) {
                    return subscriptions[0];
                }
                return undefined;
            }
        );
    }

    public follow(packageSummary: Package, feed: Feed): Promise<NotificationSubscription> {
        if (!packageSummary || !feed) {
            return Promise.reject("Invalid arguments");
        }

        const artifactId: string = this.getArtifactId(feed, packageSummary);
        const subscriptionObj: NotificationSubscription = this.createSubscriptionObject(
            FollowsDataService.ArtifactType,
            artifactId
        );

        return this.NotificationRestClient.createSubscription(subscriptionObj).then(
            (subscription: NotificationSubscription) => {
                return subscription;
            }
        );
    }

    public unfollow(subscriptionId: string): Promise<void> {
        if (subscriptionId == null || subscriptionId === "") {
            return Promise.reject("Invalid argument");
        }

        return this.NotificationRestClient.deleteSubscription(subscriptionId);
    }

    private getArtifactId(feed: Feed, packageSummary: Package): string {
        let artifactId: string;
        if (feed.view) {
            artifactId = feed.id + "@" + feed.view.id + "/" + packageSummary.id;
        } else {
            artifactId = feed.id + "/" + packageSummary.id;
        }
        return artifactId;
    }

    private get NotificationRestClient() {
        return this.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient", {
            serviceInstanceType: FeedServiceInstanceId
        });
    }

    // getSubscriptionQuery() and createSubscriptionObject() are copied from
    // \Notifications\Web\extensions\vss-notifications-web\notifications-common\Services\FollowsService.ts

    /**
     * Gets a subscription query filled in with the specified parameters
     * @param artifactType Type of artifact to query for
     * @param artifactId Specific artifact id, if any.  If not specified will return all subscriptions for the
     *                      specified artifact type.
     */
    private getSubscriptionQuery(artifactType: string, artifactId?: string): SubscriptionQuery | null {
        // get the current user id.
        const userId: string | null = this.getUserId();

        if (!userId) {
            return null;
        }

        const wif = {
            artifactId,
            artifactType,
            type: FollowsDataService.FollowsType
        } as ArtifactFilter;

        const wic = {
            subscriptionId: null,
            filter: wif,
            scope: null,
            subscriberId: userId,
            flags: null
        } as SubscriptionQueryCondition;

        return { conditions: [wic], queryFlags: SubscriptionQueryFlags.None };
    }

    /**
     * Creates a new subscription object to follow a specific artifact
     */
    private createSubscriptionObject(artifactType: string, artifactId: string): NotificationSubscription {
        const filter = {
            artifactId,
            artifactType,
            type: FollowsDataService.FollowsType
        } as ArtifactFilter;

        const notificationSubscription: NotificationSubscription = {
            diagnostics: null,
            _links: null,
            adminSettings: null,
            channel: null,
            flags: null,
            lastModifiedBy: null,
            modifiedDate: null,
            scope: null,
            status: null,
            statusMessage: "",
            // tslint:disable-next-line:no-any
            subscriber: <any>this.getUserId(),
            url: "",
            userSettings: null,
            description: "",
            extendedProperties: null,
            id: undefined,
            filter,
            permissions: null
        };

        return <NotificationSubscription>notificationSubscription;
    }

    /**
     * Gets the current user's id from the page context, or null if it cannot get it.
     */
    private getUserId(): string | null {
        const pageService: IVssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData: IPageData | undefined = pageService.getData();

        if (pageData) {
            return pageData.user.id;
        } else {
            // something is broken if we don't have a user id.
            return null;
        }
    }
}

Services.add("IFeedFollowsDataService", { serviceFactory: FollowsDataService });
